import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import Drawer from "../../components/ui/Drawer";
import { ButtonGroup, Button } from "../../components/ui/buttons/Button";

import "./file-upload.scss";
import WalletService from "../../api/wallet-service";
import { WALLET_API_URL } from "../../api/urlMetaData";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { FileUpload } from "../../components/FileUpload";
import { APP_USERS } from "../../consts/AppRoles";
import { OnlyFor } from "../../components/Roles";
import { useLocale } from "../../hooks/useLocale";

const PrefundRequestModal = ({ id, title, onClose }) => {
  const currentUser = useSelector((s) => s.auth.currentUser);
  const currentProgram = useSelector((s) => s.global.currentProgram);
  const [loading, setLoading] = useState(false);

  const { localize } = useLocale(["cards"]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    reset();
  }, []);

  const requestPrefund = async (formData) => {
    setLoading(true);

    const url =
      currentUser.productType === APP_USERS.CLIENT
        ? `${WALLET_API_URL.PREFUND_REQUEST_URL}${currentUser?.clientHashId}/program/${currentProgram}/prefundWithImage`
        : currentUser.productType === APP_USERS.SUPER_DISTRIBUTOR
        ? `${WALLET_API_URL.PREFUND_REQUEST_URL}${currentUser?.userHashId}/program/${currentProgram}/prefundWithImage`
        : `${WALLET_API_URL.PREFUND_REQUEST_URL}${currentUser?.userHashId}/program/${currentProgram}/prefundWithImage/disAndAgent`;

    try {
      const res = await WalletService.post(url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res) {
        reset();
        toast(res.data.message, {
          type: "success",
        });
        setLoading(false);
        onClose();
      }
    } catch (error) {
      onClose();
      setLoading(false);
    }
  };

  const handleFormSubmit = (formData) => {
    const data = new FormData();
    data.append("amount", formData.amount);
    data.append("bankReferenceNumber", formData.bankReferenceNumber);
    data.append("internalReferenceNumber", formData.internalReferenceNumber);
    data.append("currencyCode", formData.currencyCode);
    data.append("comments", formData.comments);
    data.append("userType", currentUser.productType);
    data.append("dateOfTransfer", new Date().toISOString().slice(0, 10));
    data.append("ownerHashId", currentUser.onboardUserHashId);
    if (formData.file) data.append("file", formData.file[0]);

    requestPrefund(data);
  };

  return (
    <>
      <Drawer title={title} id={id} onClose={onClose}>
        <>
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <fieldset disabled={loading}>
              <div className="row">
                {/* Select Currency */}
                <div className="col-sm-12 col-md-12 col-lg-6">
                  <div className="mb-3">
                    <label
                      for="requestCurrnecy"
                      className="form-label text-capitalize"
                    >
                      {localize("requestPrefund.form.currency")}
                    </label>
                    <select
                      id="requestCurrnecy"
                      className={`form-control ${
                        errors.currencyCode && "is-invalid"
                      }`}
                      {...register("currencyCode", {
                        required: {
                          value: true,
                          message: "Required Field",
                        },
                      })}
                    >
                      <option value="AED">AED</option>
                    </select>
                    <div className="invalid-feedback">
                      {errors.currencyCode?.message}
                    </div>
                  </div>
                </div>

                {/* Enter Amount */}
                <div className="col-sm-12 col-md-12 col-lg-6">
                  <div className="mb-3">
                    <label for="amount" className="form-label text-capitalize">
                      {localize("requestPrefund.form.amount")}
                    </label>
                    <input
                      type="text"
                      id="amount"
                      className={`form-control ${
                        errors.amount && "is-invalid"
                      }`}
                      {...register("amount", {
                        required: {
                          value: true,
                          message: "Required Field",
                        },
                        pattern: {
                          value: /^(?!0(?:\.0{1,2})?$)\d+(\.\d{1,2})?$/,
                          message:
                            "Only positive numbers (up to 2 decimal places are allowed)",
                        },
                      })}
                    />
                    <div className="invalid-feedback">
                      {errors.amount?.message}
                    </div>
                  </div>
                </div>

                {/* Bank Reference No. */}
                <div className="col-sm-12 col-md-12 col-lg-6">
                  <div className="mb-3">
                    <label
                      for="requestBankRefNo"
                      className="form-label text-capitalize"
                    >
                      {localize("requestPrefund.form.bankRef")}
                    </label>
                    <input
                      type="text"
                      id="requestBankRefNo"
                      className={`form-control ${
                        errors.bankReferenceNumber && "is-invalid"
                      }`}
                      {...register("bankReferenceNumber", {
                        required: {
                          value: true,
                          message: "Required Field",
                        },
                        maxLength: {
                          value: 255,
                          message: "Cannot exceed more than 255 characters",
                        },
                      })}
                    />
                    <div className="invalid-feedback">
                      {errors.bankReferenceNumber?.message}
                    </div>
                  </div>
                </div>

                {/* Internal Reference No. */}
                <div className="col-sm-12 col-md-12 col-lg-6">
                  <div className="mb-3">
                    <label
                      for="requestinternalRefNo"
                      className="form-label text-capitalize"
                    >
                      {localize("requestPrefund.form.internalRef")}
                    </label>
                    <input
                      type="text"
                      id="requestinternalRefNo"
                      className={`form-control ${
                        errors.internalReferenceNumber && "is-invalid"
                      }`}
                      {...register("internalReferenceNumber", {
                        required: {
                          value: true,
                          message: "Required Field",
                        },
                        maxLength: {
                          value: 255,
                          message: "Cannot exceed more than 255 characters",
                        },
                      })}
                    />
                    <div className="invalid-feedback">
                      {errors.internalReferenceNumber?.message}
                    </div>
                  </div>
                </div>

                {/* Add Notes */}
                <div className="col-sm-12 col-md-12 col-lg-12">
                  <div className="mb-3">
                    <label
                      for="comments"
                      className="form-label text-capitalize"
                    >
                      {localize("requestPrefund.form.addNote")}
                    </label>
                    <textarea
                      rows={3}
                      id="comments"
                      className={`form-control ${
                        errors.comments && "is-invalid"
                      }`}
                      {...register("comments", {
                        maxLength: {
                          value: 255,
                          message: "Cannot exceed more than 255 characters",
                        },
                      })}
                    ></textarea>
                    <div className="invalid-feedback">
                      {errors.comments?.message}
                    </div>
                  </div>
                </div>

                {/* Upload DOcuments */}
                <OnlyFor
                  roles={[APP_USERS.CLIENT, APP_USERS.SUPER_DISTRIBUTOR]}
                  role={currentUser.productType}
                >
                  <div className="col-sm-12 col-md-12 col-lg-12">
                    <FileUpload
                      register={register}
                      name="file"
                      error={errors["file"]}
                      title={localize("requestPrefund.form.upload")}
                      required
                    />
                  </div>
                </OnlyFor>
              </div>
            </fieldset>

            <ButtonGroup>
              <Button
                loading={loading}
                className="btn btn-primary"
                text={localize("requestPrefund.form.submitButton")}
              />
              <Button
                className="btn btn-light"
                text={localize("common.buttons.cancel")}
                onClick={onClose}
              />
            </ButtonGroup>
          </form>
        </>
      </Drawer>
    </>
  );
};

export default PrefundRequestModal;
